import {
  useId,
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react';

/**
 * A modal dialog.
 *
 * Built on the native `<dialog>`, opened with `showModal()`, because that one
 * call is everything a hand-rolled overlay gets wrong: the browser puts the
 * dialog in the top layer (no z-index race with the sidebar or the toasts),
 * draws the `::backdrop`, and makes the page behind it inert, so neither Tab
 * nor a screen reader's virtual cursor can wander out of it. What is left to
 * write is the wiring:
 *
 *  - Controlled by the caller. Escape, a click on the backdrop and the header's
 *    close button all call `onClose` and do nothing else; the dialog goes away
 *    when the caller sets `open` to false, and not before. A dialog that closed
 *    itself would leave the caller's state saying "open" and the next render
 *    disagreeing with the screen.
 *  - Escape is taken on `keydown` and its default prevented, rather than left
 *    to the browser's `cancel`. A browser that has seen two Escapes without a
 *    click in between closes the dialog whatever `cancel` says, which is the
 *    self-closing dialog above by another route. `cancel` is still handled, for
 *    the close requests that are not a key (a phone's back gesture).
 *  - Focus goes in on open -- to the first control in the body, so a search
 *    dialog is ready to type into, else to the close button -- and back to
 *    whatever had it on close. The browser does both in principle; doing them
 *    here makes them true in every browser and in the tests.
 *  - The body is only mounted while the dialog is open, so a form inside it
 *    starts empty every time and is not lying hidden in the page meanwhile.
 *  - The close button is `type="button"` and the dialog has no
 *    `method="dialog"` form of its own, so a `Form` in the body submits to its
 *    own handler and Enter in a field never reaches anything else.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  /** What the dialog is called: its heading, and its accessible name. */
  title: string;
  children: ReactNode;
  /** The footer's buttons, laid out like a form's. */
  actions?: ReactNode;
}) {
  if (!open) {
    return null;
  }
  return (
    <OpenModal onClose={onClose} title={title} actions={actions}>
      {children}
    </OpenModal>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function OpenModal({
  onClose,
  title,
  children,
  actions,
}: {
  onClose: () => void;
  title: string;
  children: ReactNode;
  actions: ReactNode;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Where a press started. A click whose press began inside the panel and was
  // released on the backdrop -- selecting text in a field and overshooting --
  // is not a click on the backdrop.
  const pressedOnBackdrop = useRef(false);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) {
      return undefined;
    }
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.showModal();
    const first = bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? closeRef.current;
    first?.focus();
    return () => {
      if (dialog.open) {
        dialog.close();
      }
      if (opener?.isConnected === true) {
        opener.focus();
      }
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
  };

  const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    onClose();
  };

  // The panel fills the `<dialog>` edge to edge (it has no padding of its
  // own), so the only place a press can land on the element itself is the
  // backdrop around it.
  const handleMouseDown = (event: MouseEvent<HTMLDialogElement>) => {
    pressedOnBackdrop.current = event.target === event.currentTarget;
  };

  const handleClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget && pressedOnBackdrop.current) {
      onClose();
    }
    pressedOnBackdrop.current = false;
  };

  return (
    <dialog
      ref={dialogRef}
      className="modal"
      aria-labelledby={titleId}
      onKeyDown={handleKeyDown}
      onCancel={handleCancel}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      <div className="modal__panel">
        <div className="modal__header">
          <h2 className="modal__title" id={titleId}>
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="modal__close"
            aria-label="Fechar"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="modal__body" ref={bodyRef}>
          {children}
        </div>
        {actions !== undefined && actions !== null && (
          <div className="modal__footer form__actions">{actions}</div>
        )}
      </div>
    </dialog>
  );
}
